/* eslint-disable formatjs/no-literal-string-in-jsx */
/* @jsxImportSource @emotion/react */
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePageContext } from 'vike-react/usePageContext'

import { Button } from '@slite/components'
import { BurgerMenuIcon, CrossIcon } from '@slite/icons'

import { SlitePage } from '../../components/SlitePage'
import { mediaQueryMedium } from '../../components/style'
import { useBanner } from '../../components/useBanner'
import type {
  HelpArticleHeading,
  HelpNoteSummary,
  HelpNoteTreeNode,
} from '../../data/helpCenterFormat'
import { HelpSearchModal } from './HelpSearchModal'
import { HelpSidebar } from './HelpSidebar'
import { HelpToc } from './HelpToc'
import {
  HELP_NAVBAR_HEIGHT,
  HELP_PAGE_MAX_WIDTH,
  HELP_SIDEBAR_WIDTH,
  HELP_TOC_WIDTH,
  helpMediaTocHidden,
} from './helpTheme'
import { HelpSearchProvider } from './useHelpSearch'

export interface HelpNav {
  root: HelpNoteSummary
  categories: HelpNoteTreeNode[]
  currentNoteId: string
  expandedIds: string[]
}

// Only shows on mobile, where the sidebar tree is collapsed; sits above the
// content box (not inside it) as a clear, tappable control.
function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="tertiary"
      className="margin-bottom-12"
      css={{
        display: 'none',
        [mediaQueryMedium]: { display: 'inline-flex' },
      }}
      onClick={onClick}
      icon={BurgerMenuIcon}
    >
      <FormattedMessage
        id="website.helpCenter.mobileMenu2"
        defaultMessage="Browse articles"
      />
    </Button>
  )
}

// A sticky nav/TOC column that fills the viewport and scrolls on its own, with
// a gradient at the bottom edge fading its content into the canvas.
function StickyColumn({
  width,
  padding,
  top,
  height,
  hiddenAt,
  children,
}: {
  width: number
  padding: string
  top: number
  height: string
  hiddenAt: string
  children: React.ReactNode
}) {
  return (
    <aside
      className="flexbox-no-shrink"
      css={{
        width,
        boxSizing: 'border-box',
        position: 'sticky',
        top,
        height,
        [hiddenAt]: { display: 'none' },
      }}
    >
      <div css={{ position: 'relative', height: '100%' }}>
        <div css={{ height: '100%', overflowY: 'auto', padding }}>
          {children}
        </div>
        <div
          aria-hidden
          css={{
            position: 'absolute',
            insetInline: 0,
            bottom: 0,
            height: 40,
            background:
              'linear-gradient(transparent, var(--moon-dust-lighter))',
            pointerEvents: 'none',
          }}
        />
      </div>
    </aside>
  )
}

// The app-like frame: the sidebar and TOC are sticky columns that fill the
// viewport, the content lives in a white box beside them, and the whole page
// scrolls — so the site's full-width footer sits at the very bottom, reached by
// scrolling past the columns, exactly like every other marketing page.
export function HelpShell({
  nav,
  toc,
  contentWidth,
  title,
  description,
  canonicalUrl,
  children,
}: {
  nav: HelpNav
  toc: HelpArticleHeading[]
  contentWidth: number
  title: string
  description: string
  canonicalUrl: string
  children: React.ReactNode
}) {
  const intl = useIntl()
  const [isSearchOpen, setSearchOpen] = React.useState(false)
  const [isMobileNavOpen, setMobileNavOpen] = React.useState(false)
  // Same offset SlitePage applies to the navbar when the promo banner shows.
  const { navbarTopOffset } = useBanner()
  const stickyTop = HELP_NAVBAR_HEIGHT + navbarTopOffset
  const columnHeight = `calc(100vh - ${stickyTop}px)`

  const openSearch = React.useCallback(() => {
    setMobileNavOpen(false)
    setSearchOpen(true)
  }, [])

  // Navigation is client-side and this shell stays mounted between articles,
  // so overlays have to be dismissed explicitly when the page changes.
  const { urlPathname } = usePageContext()
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs on every navigation, keyed by pathname
  React.useEffect(() => {
    setSearchOpen(false)
    setMobileNavOpen(false)
  }, [urlPathname])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        setSearchOpen((previous) => !previous)
      } else if (event.key === 'Escape') {
        setSearchOpen(false)
        setMobileNavOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const sidebar = <HelpSidebar {...nav} />

  return (
    <SlitePage
      title={title}
      description={description}
      canonicalUrl={canonicalUrl}
      background="moon-dust-lighter"
      navbarMaxWidth={HELP_PAGE_MAX_WIDTH}
    >
      <HelpSearchProvider value={openSearch}>
        <div css={{ background: 'var(--moon-dust-lighter)' }}>
          <div
            className="flexbox flexbox-align-start"
            css={{
              maxWidth: HELP_PAGE_MAX_WIDTH,
              margin: '0 auto',
              padding: '0 24px',
              minHeight: columnHeight,
              [mediaQueryMedium]: { padding: '0 16px' },
            }}
          >
            <StickyColumn
              width={HELP_SIDEBAR_WIDTH}
              padding="0 16px 24px 0"
              top={stickyTop}
              height={columnHeight}
              hiddenAt={mediaQueryMedium}
            >
              {sidebar}
            </StickyColumn>

            {isMobileNavOpen && (
              <div css={{ position: 'fixed', inset: 0, zIndex: 1050 }}>
                <div
                  css={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(38, 42, 48, 0.4)',
                  }}
                  onClick={() => setMobileNavOpen(false)}
                />
                <div
                  className="padding-12"
                  css={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: HELP_SIDEBAR_WIDTH,
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                    background: 'var(--moon-dust-lighter)',
                    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.12)',
                  }}
                >
                  <button
                    type="button"
                    className="flexbox flexbox-centered border-none background-transparent cursor-pointer shade-30 shade-05-on-hover padding-4 margin-bottom-8"
                    onClick={() => setMobileNavOpen(false)}
                    aria-label={intl.formatMessage({
                      id: 'website.helpCenter.closeNavigation',
                      defaultMessage: 'Close navigation',
                    })}
                  >
                    <CrossIcon size={16} />
                  </button>
                  {sidebar}
                </div>
              </div>
            )}

            {/* Content column: the mobile "browse" trigger sits above the white
                box (never inside it), and the box stretches to fill height. */}
            <div
              className="flexbox flexbox-column flexbox-grow min-width-zero"
              css={{ alignSelf: 'stretch', margin: '16px 0 24px' }}
            >
              <MobileMenuButton onClick={() => setMobileNavOpen(true)} />
              <div
                className="flexbox flexbox-grow"
                css={{
                  background: 'var(--shade-100)',
                  border: '1px solid var(--shade-45)',
                  borderRadius: 16,
                  // Fill the screen on short pages instead of shrinking to content.
                  minHeight: `calc(100vh - ${stickyTop + 40}px)`,
                }}
              >
                <main
                  css={{
                    width: '100%',
                    maxWidth: contentWidth,
                    margin: '0 auto',
                    padding: '64px 24px 96px',
                    boxSizing: 'border-box',
                    [mediaQueryMedium]: { padding: '48px 16px 24px' },
                    '& .help-article-content :is(h1, h2, h3, h4)': {
                      scrollMarginTop: stickyTop + 24,
                    },
                  }}
                >
                  {children}
                </main>
              </div>
            </div>

            {/* Always render the TOC column, even with no headings, so the
                content box keeps the same width across every help page. */}
            <StickyColumn
              width={HELP_TOC_WIDTH}
              padding="0 0 32px 32px"
              top={stickyTop}
              height={columnHeight}
              hiddenAt={helpMediaTocHidden}
            >
              <HelpToc
                headings={toc}
                scanLineOffset={stickyTop + 32}
                scrollContainer={null}
              />
            </StickyColumn>
          </div>
        </div>
        <HelpSearchModal
          isOpen={isSearchOpen}
          onClose={() => setSearchOpen(false)}
        />
      </HelpSearchProvider>
    </SlitePage>
  )
}
